import moment from 'moment';
import { HStack, VStack } from 'native-base';
import { useTranslation } from 'react-i18next';

import { PrinterIcon } from '../icons/printer';
import { OpsbeeWordmark } from '../icons/opsbee-wordmark';
import { Button, Text } from '@pimm/base';
import { buildExportFileName, fmtLastSeen } from '../_helper';
import type { HubMeta, SensorMeta, SensorRow, TableMode } from '../types';
import { LinkIcon } from '../icons/link';
import { HardwareTypeIcon } from './hardware-type-icon';
import type { SensorColumnDef } from './sensor-table';

type TFn = (key: string) => string;

const GROUP_ORDER = ['Gateways', 'Walk-ins', 'Coolers', 'Freezers'];
const OTHER_GROUP = 'Other';
const groupRank = (g: string) => {
  const i = GROUP_ORDER.indexOf(g);
  return i === -1 ? GROUP_ORDER.length : i;
};
const GROUP_KEY: Record<string, string> = {
  Gateways: 'gateways',
  'Walk-ins': 'walk_ins',
  Coolers: 'coolers_group',
  Freezers: 'freezers',
  Other: 'other',
};
const groupLabel = (t: TFn, g: string) => (GROUP_KEY[g] ? t(`store_equipment:${GROUP_KEY[g]}`) : g);

type FlatRow = { type: 'group'; group: string } | { type: 'sensor'; sensor: SensorRow } | { type: 'gateway'; hub: HubMeta };

const buildFlatRows = (sensors: SensorRow[], hubs: HubMeta[]): FlatRow[] => {
  const byGroup = new Map<string, FlatRow[]>();
  const push = (group: string, row: FlatRow) => {
    if (!byGroup.has(group)) byGroup.set(group, []);
    byGroup.get(group)!.push(row);
  };
  sensors.forEach(s => push(s.displayGroup || OTHER_GROUP, { type: 'sensor', sensor: s }));
  hubs.forEach(h => push('Gateways', { type: 'gateway', hub: h }));
  const out: FlatRow[] = [];
  [...byGroup.keys()]
    .sort((x, y) => groupRank(x) - groupRank(y) || x.localeCompare(y))
    .forEach(group => {
      out.push({ type: 'group', group });
      out.push(...byGroup.get(group)!);
    });
  return out;
};

export type SiteInfo = {
  name: string;
  customerName: string;
  address: string;
  logoUrl?: string;
};

type Props = {
  onClose: () => void;
  sensors: SensorRow[];
  hubs: HubMeta[];
  columns: SensorColumnDef[];
  sensorMeta: Record<string, SensorMeta>;
  mode: TableMode;
  siteInfo?: SiteInfo;
  generatedBy?: string;
  periodLabel?: string;
  rangeDays?: number;
  scope?: 'current' | 'full';
  quickFilterLabel?: string;
  groupFilterLabel?: string;
  deviceType?: string;
};

const FIRST_PAGE_ROWS = 14;
const ROWS_PER_PAGE = 24;

const PRINT_WIDTH: Record<string, number> = {
  sensor: 190,
  model: 110,
  serial: 120,
  status: 72,
  battery: 92,
  lastSeen: 96,
  uptime: 60,
  upload: 68,
  offline: 64,
  oos: 60,
  offEvts: 64,
  batteryEvts: 68,
  avgTemp: 64,
  inTemp: 60,
  tempEvts: 70,
  doorEvts: 64,
};

const PRINT_STYLES = `
  .equipment-report-screen-wrap { background: #f3f4f6; padding: 24px 0; overflow-y: auto; max-height: calc(90vh - 56px); }
  .equipment-report-paper {
    width: 11in; height: 8.5in; margin: 0 auto; background: white;
    box-shadow: 0 2px 12px rgba(15,23,42,0.12); padding: 0.5in 0.55in; box-sizing: border-box;
    position: relative; display: flex; flex-direction: column; overflow: hidden; font-family: 'Inter', sans-serif;
  }
  .equipment-report-paper + .equipment-report-paper { margin-top: 24px; }
  .equipment-report-paper-body { flex: 1 1 auto; min-height: 0; overflow: hidden; }
  .equipment-report-page-header { display: flex; justify-content: space-between; align-items: center; padding-bottom: 6px; margin-bottom: 10px; flex-shrink: 0; }
  .equipment-report-page-footer {
    display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; column-gap: 16px;
    padding-top: 4px; margin-top: 4px; font-size: 9px; color: #6b7280; flex-shrink: 0;
  }
  .equipment-report-page-footer > span:nth-child(1) { justify-self: start; }
  .equipment-report-page-footer > span:nth-child(2) { justify-self: center; }
  .equipment-report-page-footer > span:nth-child(3) { justify-self: end; text-align: right; }
  .equipment-report-section-bar { background: #f1f3f5; border-radius: 3px; padding: 5px 10px; font-size: 12px; font-weight: 700; color: #111827; margin-bottom: 8px; }
  .equipment-report-store-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; column-gap: 18px; row-gap: 8px; margin-bottom: 2px; }
  .equipment-report-store-cell-label { font-size: 8px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 1px; }
  .equipment-report-store-cell-value { font-size: 11px; font-weight: 600; color: #111827; line-height: 1.3; }
  .equipment-report-params { display: flex; flex-wrap: wrap; justify-content: flex-start; gap: 2px 16px; font-size: 10px; color: #4b5563; margin: 6px 0 0; background: #ffffff; border-radius: 4px; padding: 5px 8px; }
  .equipment-report-params .k { font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: 0.4px; margin-right: 4px; }
  .equipment-report-params .v { font-weight: 600; color: #111827; }
  .equipment-report-grid-table { width: 100%; border-collapse: collapse; font-family: 'Inter', sans-serif; table-layout: fixed; }
  .equipment-report-grid-table th { text-align: left; font-size: 9.5px; font-weight: 700; color: #6b7280; padding: 6px 8px; border-bottom: 1px solid #e5e7eb; white-space: normal; vertical-align: bottom; line-height: 1.15; }
  .equipment-report-grid-table td { font-size: 10.5px; color: #111827; padding: 5px 8px; border-bottom: 1px solid #f3f4f6; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .equipment-report-grid-table tr:nth-child(even) td { background: #fafafa; }
  .equipment-report-grid-section td { background: #f1f3f5 !important; font-size: 10px; font-weight: 700; color: #4b5563; padding: 4px 8px; border-top: 1px solid #e5e7eb; border-bottom: 1px solid #e5e7eb; }
  .equipment-report-cell-bold { font-weight: 600; }
  .equipment-report-cell-muted { color: #9ca3af; }
  @media print {
    @page { size: letter landscape; margin: 0; }
    html, body { background: white !important; margin: 0 !important; height: auto !important; overflow: visible !important; }
    body > *:not(.equipment-report-print-modal) { display: none !important; }
    .equipment-report-print-modal { display: block !important; position: static !important; width: 100% !important; height: auto !important; max-height: none !important; overflow: visible !important; background: white !important; box-shadow: none !important; border-radius: 0 !important; padding: 0 !important; margin: 0 !important; }
    .equipment-report-print-chrome { display: none !important; }
    .equipment-report-screen-wrap { padding: 0 !important; background: white !important; overflow: visible !important; max-height: none !important; }
    .equipment-report-paper { box-shadow: none !important; margin: 0 !important; }
    .equipment-report-paper + .equipment-report-paper { margin-top: 0 !important; page-break-before: always; break-before: page; }
  }
`;

const reportTitle = (t: TFn, mode: TableMode) =>
  mode === 'diagnostics' ? t('store_equipment:equipment_diagnostics_report') : t('store_equipment:equipment_performance_report');

const dataSectionLabel = (t: TFn, mode: TableMode) =>
  mode === 'diagnostics' ? t('store_equipment:diagnostic_data') : t('store_equipment:performance_data');

const gatewayText = (t: TFn, colId: string, hub: HubMeta): string => {
  switch (colId) {
    case 'sensor':
      return hub.label + (hub.isPrimary ? ` (${t('store_equipment:primary')})` : '');
    case 'hardwareType':
      return t('store_equipment:gateway');
    case 'model':
      return hub.model ?? hub.type ?? '—';
    case 'serial':
      return hub.serial;
    case 'status':
      return t(`store_equipment:${hub.online === true ? 'online' : hub.online === false ? 'offline' : 'no_data'}`);
    case 'lastSeen':
      return fmtLastSeen(hub.lastReportedTime);
    default:
      return '—';
  }
};

export const SensorPrintView = ({
  onClose,
  sensors,
  hubs,
  columns,
  sensorMeta,
  mode,
  siteInfo,
  generatedBy,
  periodLabel = '',
  rangeDays = 0,
  scope = 'current',
  quickFilterLabel = '',
  groupFilterLabel = '',
  deviceType,
}: Props) => {
  const { t } = useTranslation();

  const handlePrint = () => {
    const modeName = mode === 'diagnostics' ? 'Diagnostics' : 'Performance';
    const periodText = rangeDays > 0 ? `Last ${rangeDays} Days` : 'Current';
    const reportName = `Equipment ${modeName} - ${periodText}${scope === 'full' ? ' (Full Report)' : ''}`;
    const prevTitle = document.title;
    document.title = buildExportFileName(siteInfo?.name, reportName);

    const node = document.querySelector('.equipment-report-print-modal');
    const placeholder = node?.parentNode ? document.createComment('equip-print-modal-placeholder') : null;
    if (node?.parentNode && placeholder) {
      node.parentNode.insertBefore(placeholder, node);
      document.body.appendChild(node);
    }
    const restore = () => {
      window.removeEventListener('afterprint', restore);
      document.title = prevTitle;
      if (placeholder?.parentNode) {
        placeholder.parentNode.insertBefore(node!, placeholder);
        placeholder.parentNode.removeChild(placeholder);
      }
    };
    window.addEventListener('afterprint', restore);
    window.print();
  };

  const rangeStart = moment().subtract(rangeDays, 'days');
  const headerDate =
    rangeDays > 0
      ? `${rangeStart.format(rangeStart.year() === moment().year() ? 'MMM D' : 'MMM D, YYYY')} – ${moment().format('MMM D, YYYY')}`
      : moment().format('MMM D, YYYY');
  const generatedAt = moment().format('MMM D, YYYY h:mm A');

  const metaVals = Object.values(sensorMeta ?? {});
  const tempCount = metaVals.filter(m => m?.sensorType === 'temp').length;
  const doorCount = metaVals.filter(m => m?.sensorType === 'door').length;

  const flatRows = buildFlatRows(sensors, hubs);

  const pages: FlatRow[][] = (() => {
    if (flatRows.length === 0) return [];
    const out: FlatRow[][] = [];
    let page: FlatRow[] = [];
    let currentGroup: FlatRow | null = null;
    const cap = () => (out.length === 0 ? FIRST_PAGE_ROWS : ROWS_PER_PAGE);
    const flush = () => {
      if (page.length) {
        out.push(page);
        page = [];
      }
    };
    for (const row of flatRows) {
      if (row.type === 'group') {
        currentGroup = row;
        if (page.length + 2 > cap()) flush();
        page.push(row);
      } else {
        if (page.length + 1 > cap()) {
          flush();
          if (currentGroup) page.push(currentGroup);
        }
        page.push(row);
      }
    }
    flush();
    return out;
  })();
  const totalPages = Math.max(1, pages.length);

  const renderPageHeader = () => (
    // @ts-ignore
    <div className="equipment-report-page-header">
      <HStack alignItems="center" space={3} flex={1}>
        <HStack alignItems="center" space={2}>
          {!!siteInfo?.logoUrl && (
            // @ts-ignore
            <img src={siteInfo.logoUrl} style={{ height: 40, objectFit: 'contain' }} />
          )}
          <VStack>
            <Text size="lg" fontWeight={700} color="black" textTransform="uppercase" letterSpacing={0.3} lineHeight="xs">
              {reportTitle(t, mode)}
            </Text>
            <Text size="sm" color="gray.700" fontWeight={500} lineHeight="xs">
              {periodLabel}
            </Text>
          </VStack>
        </HStack>
      </HStack>
      <VStack alignItems="flex-end" space={0.5}>
        <Text size="xs" color="gray.700" fontWeight={600}>
          {headerDate}
        </Text>
        <Text size="xs" color="gray.700" fontWeight={600}>
          {siteInfo?.name ?? ''}
        </Text>
      </VStack>
    </div>
  );

  const renderEquipInfo = () => (
    <>
      {/* @ts-ignore */}
      <div className="equipment-report-section-bar">{t('store_equipment:equipment_information')}</div>
      {/* @ts-ignore */}
      <div className="equipment-report-store-grid" style={{ marginBottom: 4 }}>
        {/* @ts-ignore */}
        <div>
          <div className="equipment-report-store-cell-label">{t('store_equipment:customer')}</div>
          <div className="equipment-report-store-cell-value">{siteInfo?.customerName ?? '—'}</div>
        </div>
        {/* @ts-ignore */}
        <div>
          <div className="equipment-report-store-cell-label">{t('store_equipment:store')}</div>
          <div className="equipment-report-store-cell-value">{siteInfo?.name ?? '—'}</div>
        </div>
        {/* @ts-ignore */}
        <div>
          <div className="equipment-report-store-cell-label">{t('store_equipment:address')}</div>
          <div className="equipment-report-store-cell-value">{siteInfo?.address ?? '—'}</div>
        </div>
        {/* @ts-ignore */}
        <div>
          <div className="equipment-report-store-cell-label">{t('store_equipment:hardware_type')}</div>
          <div className="equipment-report-store-cell-value">{deviceType ?? '—'}</div>
        </div>
        {/* @ts-ignore */}
        <div>
          <div className="equipment-report-store-cell-label">{t('store_equipment:temperature_sensors')}</div>
          <div className="equipment-report-store-cell-value">{tempCount}</div>
        </div>
        {/* @ts-ignore */}
        <div>
          <div className="equipment-report-store-cell-label">{t('store_equipment:door_sensors')}</div>
          <div className="equipment-report-store-cell-value">{doorCount}</div>
        </div>
      </div>
    </>
  );

  const renderParams = () => {
    const pair = (k: string, v: string) => (
      // @ts-ignore
      <span>
        <span className="k">{k}</span>
        <span className="v">{v}</span>
      </span>
    );
    return (
      // @ts-ignore
      <div className="equipment-report-params">
        {pair('Period', periodLabel || '—')}
        {scope === 'current' && (
          <>
            {pair('Filter', quickFilterLabel || t('store_equipment:all'))}
            {pair('Groups', groupFilterLabel || t('store_equipment:all_groups'))}
          </>
        )}
      </div>
    );
  };

  const renderDataSectionBar = (firstPage: boolean) => (
    // @ts-ignore
    <div className="equipment-report-section-bar" style={firstPage ? { marginTop: 18 } : undefined}>
      {/* @ts-ignore */}
      <div>{dataSectionLabel(t, mode)}</div>
      {firstPage && renderParams()}
    </div>
  );

  const renderPageFooter = (pageNumber: number) => (
    // @ts-ignore
    <div className="equipment-report-page-footer">
      {/* @ts-ignore */}
      <span>
        <OpsbeeWordmark height={17} />
      </span>
      {/* @ts-ignore */}
      <span>{`${t('store_equipment:generated_at')} ${generatedAt}${generatedBy ? ` · ${generatedBy}` : ''}`}</span>
      {/* @ts-ignore */}
      <span>
        {t('store_equipment:page')} {pageNumber} {t('store_equipment:of')} {totalPages}
      </span>
    </div>
  );

  const renderRow = (row: FlatRow, i: number) => {
    if (row.type === 'group') {
      return (
        // @ts-ignore
        <tr key={`group-${row.group}-${i}`} className="equipment-report-grid-section">
          {/* @ts-ignore */}
          <td colSpan={columns.length}>{groupLabel(t, row.group)}</td>
        </tr>
      );
    }
    if (row.type === 'gateway') {
      return (
        // @ts-ignore
        <tr key={`gw-${row.hub.serial}-${i}`}>
          {columns.map((c, ci) => (
            // @ts-ignore
            <td key={c.id} className={ci === 0 ? 'equipment-report-cell-bold' : ''}>
              {ci === 0 ? (
                // @ts-ignore
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <HardwareTypeIcon type="gateway" size="xs" />
                  {gatewayText(t, c.id, row.hub)}
                </span>
              ) : (
                gatewayText(t, c.id, row.hub)
              )}
            </td>
          ))}
        </tr>
      );
    }
    const { sensor: s } = row;
    const meta = sensorMeta[s.label];
    return (
      // @ts-ignore
      <tr key={s.label}>
        {columns.map((c, ci) => {
          const text = c.renderText(s, meta);
          const cls = ci === 0 ? 'equipment-report-cell-bold' : text ? '' : 'equipment-report-cell-muted';
          return (
            // @ts-ignore
            <td key={c.id} className={cls}>
              {ci === 0 ? (
                // @ts-ignore
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <HardwareTypeIcon type={meta?.sensorType ?? 'temp'} size="xs" />
                  {text || '—'}
                  {meta?.linkedTsu ? (
                    // @ts-ignore
                    <span
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 2, marginLeft: 2, color: '#6b7280', fontWeight: 400 }}
                    >
                      <LinkIcon size={3} color="#6b7280" />
                      {/* @ts-ignore */}
                      <span style={{ fontSize: '9px' }}>{meta.linkedTsu}</span>
                    </span>
                  ) : null}
                </span>
              ) : (
                text || '—'
              )}
            </td>
          );
        })}
      </tr>
    );
  };

  const colWidth = (c: SensorColumnDef) => PRINT_WIDTH[c.id] ?? (c.width ? Math.round(c.width * 0.92) : undefined);

  const renderTable = (pageRows: FlatRow[]) => (
    // @ts-ignore
    <table className="equipment-report-grid-table">
      {/* @ts-ignore */}
      <thead>
        {/* @ts-ignore */}
        <tr>
          {columns.map(c => {
            const w = colWidth(c);
            // @ts-ignore
            return (
              <th key={c.id} style={w ? { width: w } : undefined}>
                {c.label}
              </th>
            );
          })}
        </tr>
      </thead>
      {/* @ts-ignore */}
      <tbody>{pageRows.map((row, i) => renderRow(row, i))}</tbody>
    </table>
  );

  return (
    <>
      {/* @ts-ignore */}
      <style dangerouslySetInnerHTML={{ __html: PRINT_STYLES }} />
      {/* @ts-ignore */}
      <div className="equipment-report-print-modal">
        {/* @ts-ignore */}
        <div
          className="equipment-report-print-chrome"
          style={{
            background: 'white',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            borderBottom: '1px solid #E5E7EB',
            padding: '8px 16px',
          }}
        >
          <HStack justifyContent="space-between" alignItems="center" w="full">
            <HStack space={3} alignItems="center">
              <PrinterIcon color="gray.700" size="md" />
              <Text size="lg" fontWeight={700} color="black">
                {reportTitle(t, mode)}
              </Text>
            </HStack>
            <HStack space={2}>
              <Button
                size="md"
                variant="unstyled"
                borderColor="gray.300"
                bg="white"
                _text={{ color: 'gray.900' }}
                shadow={1}
                onPress={onClose}
              >
                {t('store_equipment:close')}
              </Button>

              <Button size="md" shadow={0} startIcon={<PrinterIcon color="black" size={4} />} onPress={handlePrint}>
                {t('store_equipment:print')}
              </Button>
            </HStack>
          </HStack>
        </div>

        {/* @ts-ignore */}
        <div className="equipment-report-screen-wrap">
          {pages.length === 0 ? (
            // @ts-ignore
            <div className="equipment-report-paper">
              {renderPageHeader()}
              {/* @ts-ignore */}
              <div className="equipment-report-paper-body">
                {renderEquipInfo()}
                {renderDataSectionBar(true)}
                {/* @ts-ignore */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', paddingTop: 24 }}>
                  {t('store_equipment:no_sensor_data_available')}
                </div>
              </div>
              {renderPageFooter(1)}
            </div>
          ) : (
            pages.map((pageRows, pageIdx) => (
              // @ts-ignore
              <div key={pageIdx} className="equipment-report-paper">
                {renderPageHeader()}
                {/* @ts-ignore */}
                <div className="equipment-report-paper-body">
                  {pageIdx === 0 && renderEquipInfo()}
                  {renderDataSectionBar(pageIdx === 0)}
                  {renderTable(pageRows)}
                </div>
                {renderPageFooter(pageIdx + 1)}
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};
